import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Menu, ShoppingBag, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { Heart as HeartIcon } from "./Decor";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/meet-rachel", label: "Meet Rachel" },
  { to: "/custom-orders", label: "Custom Orders" },
];

export default function Header() {
  const { cart, settings, setCartOpen } = useStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const count = (cart.items || []).reduce((n, i) => n + i.qty, 0);

  return (
    <header className="sticky top-0 z-40">
      <div className="bg-candy-band text-white text-center py-1.5 px-4 flex items-center justify-center gap-2">
        <HeartIcon className="w-3.5 h-3.5 text-candy-deep" />
        <span data-testid="announcement-strip" className="font-hand text-lg font-semibold tracking-wide">{settings.announcement || "A little yarn. A lot of love."}</span>
        <HeartIcon className="w-3.5 h-3.5 text-candy-deep" />
      </div>
      <div className="bg-white/90 backdrop-blur border-b border-candy-soft/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
            <img src="/assets/logo.jpg" alt="Hooked by Rachel — handmade crochet" className="h-12 sm:h-14 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-1" aria-label="Main">
            {LINKS.map((l) => {
              const active = l.to === "/" ? pathname === "/" : pathname.startsWith(l.to);
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-2.5 rounded-full text-sm font-bold transition-colors duration-200 ${active ? "bg-candy-glow shadow-pinkGlow text-ink" : "text-ink/80 hover:bg-candy-soft/70"}`}
                >
                  {l.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/wishlist" data-testid="header-wishlist-link" aria-label="Wishlist" className="p-2.5 rounded-full hover:bg-candy-soft/70 transition-colors duration-200">
              <Heart className="w-5 h-5 text-candy" />
            </Link>
            <button
              data-testid="cart-drawer-toggle"
              onClick={() => setCartOpen(true)}
              aria-label={`Open cart, ${count} items`}
              className="relative p-2.5 rounded-full hover:bg-candy-soft/70 transition-colors duration-200"
            >
              <ShoppingBag className="w-5 h-5 text-ink" />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-candy text-white text-[11px] font-bold flex items-center justify-center">{count}</span>
              )}
            </button>
            <button data-testid="mobile-menu-toggle" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu" className="md:hidden p-2.5 rounded-full hover:bg-candy-soft/70">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden border-t border-candy-soft/60 bg-white"
              aria-label="Mobile"
            >
              <div className="px-4 py-3 flex flex-col gap-1">
                {LINKS.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="px-4 py-3 rounded-2xl font-bold text-ink hover:bg-candy-soft">
                    {l.label}
                  </Link>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
